import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import { CONTACT_NEEDS, type ContactNeed } from "@/lib/contact";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

/** The signal accent is reserved for this primary action. */
export const signalButtonClass =
	"bg-signal text-signal-foreground shadow-[0_8px_30px_-10px] shadow-signal/60 hover:bg-signal/90";

const SIZE_CLASS = {
	sm: "rounded-md px-4 has-[>svg]:px-4",
	default: "",
	lg: "h-auto min-h-11 rounded-lg px-6 py-2.5 whitespace-normal has-[>svg]:px-6",
	xl: "h-auto min-h-14 rounded-lg px-8 py-3.5 text-sm whitespace-normal has-[>svg]:px-8",
} as const;

/** Link to the contact page, optionally with the need preselected. */
export function ContactButton({
	need,
	placement,
	variant = "signal",
	size = "lg",
	className,
	children,
}: {
	need?: ContactNeed;
	placement: string;
	variant?: "signal" | "default" | "outline" | "secondary";
	size?: keyof typeof SIZE_CLASS;
	className?: string;
	children: React.ReactNode;
}) {
	const { localeParams } = useI18n();
	const { capture } = useAnalytics();

	return (
		<Button
			size={size === "xl" ? "lg" : size}
			variant={variant === "signal" ? "default" : variant}
			className={cn(
				"group/cta text-left font-mono text-[13px] font-medium tracking-[-0.01em]",
				SIZE_CLASS[size],
				variant === "signal" && signalButtonClass,
				className,
			)}
			asChild
		>
			<Link
				to="/{-$locale}/contact"
				params={localeParams}
				search={need ? { besoin: CONTACT_NEEDS[need] } : {}}
				onClick={() =>
					capture(ANALYTICS_EVENTS.ctaClick, {
						cta: "contact",
						placement,
						need,
					})
				}
			>
				{children}
				<ArrowRight className="transition-transform duration-300 ease-(--ease-soft) group-hover/cta:translate-x-1" />
			</Link>
		</Button>
	);
}
